<template>
  <RfDialog
    ref="dialogRef"
    title="Figma Details"
    width="700"
    keepOpenOnConfirm
    cancelText="Close"
    confirmText="Save token"
    :loading="saving"
    :disabled="!planToken.trim() || userStore.isDisabledWithMessage"
    @confirm="savePlanToken"
  >
    <div class="flex flex-col gap-6">
      <div class="flex items-center gap-2">
        <span class="text-sm font-semibold">Status:</span>
        <RfColorTag :backgroundColor="statusColor" disabled pill>
          {{ statusLabel }}
        </RfColorTag>
        <RfButtonNew
          v-if="isConnected"
          type="quaternary"
          size="xx-small"
          class="border-vermillion-50 text-vermillion-50 [&:not(:disabled)_.rf-icon]:fill-vermillion-50! ml-2"
          :loading="disconnecting"
          :disabled="userStore.isDisabledWithMessage"
          @click="disconnect"
        >
          Disconnect
        </RfButtonNew>
      </div>

      <template v-if="!isConnected">
        <div>
          <p class="text-body text-grey-1 mb-3">Connect with your Figma account via OAuth:</p>
          <RfButtonNew :href="connectUrl" text="Connect with Figma" type="secondary" size="large" />
        </div>
        <hr class="border-beige-10" />
      </template>

      <div>
        <p class="text-body text-grey-1 mb-3">
          {{ isConnected ? "Update your Figma plan token:" : "Or connect using a plan token:" }}
        </p>
        <RfInput
          v-model="planToken"
          title="PLAN TOKEN"
          placeholder="Enter Figma plan token"
          variant="outlined"
          :disabled="userStore.isDisabledWithMessage"
        />
      </div>
    </div>
  </RfDialog>
</template>

<script setup>
import RfDialog from "@/components/modals/RfDialog.vue";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import RfInput from "@/components/inputs/RfInput.vue";
import { useUserStore } from "@/pinia/userStore";
import { useToastsStore } from "@/pinia/toastsStore";
import ApiFigma from "@/apis/ApiFigma";
import { computed, ref, useTemplateRef } from "vue";
import RfColorTag from "@/components/tags/RfColorTag.vue";

const FigmaStatus = Object.freeze({
  CONNECTED: "connected",
  EXPIRED: "expired",
  DISCONNECTED: "disconnected",
});

const ToastType = Object.freeze({
  SUCCESS: "success",
  ERROR: "error",
});

const STATUS_COLOR = Object.freeze({
  [FigmaStatus.CONNECTED]: "#617935",
  [FigmaStatus.EXPIRED]: "#FFD700",
  [FigmaStatus.DISCONNECTED]: "#edebe4",
});

const STATUS_LABEL = Object.freeze({
  [FigmaStatus.CONNECTED]: "Connected",
  [FigmaStatus.EXPIRED]: "Token expired",
  [FigmaStatus.DISCONNECTED]: "Not connected",
});

const props = defineProps({
  app: { type: Object, required: true },
  figmaConnector: { type: Object, default: () => ({}) },
});

const emit = defineEmits(["refresh"]);

const userStore = useUserStore();
const toastsStore = useToastsStore();
const dialogRef = useTemplateRef("dialogRef");

const planToken = ref("");
const saving = ref(false);
const disconnecting = ref(false);

const status = computed(() => props.figmaConnector?.status || FigmaStatus.DISCONNECTED);
const isConnected = computed(() => status.value === FigmaStatus.CONNECTED);
const statusColor = computed(
  () => STATUS_COLOR[status.value] ?? STATUS_COLOR[FigmaStatus.DISCONNECTED],
);
const statusLabel = computed(() => STATUS_LABEL[status.value] ?? "Unknown");
const connectUrl = computed(() => ApiFigma.connectUrl(props.app.id));

const open = () => {
  planToken.value = "";
  dialogRef.value?.open();
};

const savePlanToken = async () => {
  saving.value = true;
  try {
    await ApiFigma.savePlanToken(props.app.id, planToken.value.trim());
    planToken.value = "";
    dialogRef.value?.close();
    emit("refresh");
    toastsStore.create({ type: ToastType.SUCCESS, body: "Figma plan token saved." });
  } catch (e) {
    toastsStore.create({ type: ToastType.ERROR, body: e.message || "Failed to save plan token." });
  } finally {
    saving.value = false;
  }
};

const disconnect = async () => {
  disconnecting.value = true;
  try {
    await ApiFigma.disconnect(props.app.id);
    dialogRef.value?.close();
    emit("refresh");
    toastsStore.create({ type: ToastType.SUCCESS, body: "Figma disconnected." });
  } catch (e) {
    toastsStore.create({ type: ToastType.ERROR, body: e.message || "Failed to disconnect." });
  } finally {
    disconnecting.value = false;
  }
};

defineExpose({ open });
</script>
